(function () {
  'use strict';

  // Mirrors the `tkc_api_endpoint` setting default (config/settings_schema.json).
  // The configured settings value is the source of truth; this is only a fallback
  // for when window.TKC.authConfig is unavailable.
  const DEFAULT_API_ENDPOINT = 'https://api-dev.tennesseeknifecompany.com/graphql';

  const ORDER_FIELDS = 'orders { id name displayFinancialStatus displayFulfillmentStatus totalPriceSet { shopMoney { amount currencyCode } } lineItems { id title quantity sku imageUrl imageAltText variantId variantTitle } createdAt cursor } pageInfo { hasNextPage endCursor }';
  const ORDER_SUMMARY_FIELDS = 'orders { id name displayFinancialStatus displayFulfillmentStatus totalPriceSet { shopMoney { amount currencyCode } } createdAt cursor } pageInfo { hasNextPage endCursor }';
  const ORDER_MINIMAL_FIELDS = 'orders { id name createdAt cursor } pageInfo { hasNextPage endCursor }';
  const ADDRESS_FIELDS = 'id firstName lastName address1 address2 city province provinceCode zip country countryCode phone company name isDefault';
  const PROFILE_USER_FIELDS = 'id name email dob avatar_url phone_number role shopify_customer_id created_at updated_at';
  const STORY_NODE_FIELDS = 'id city state harvest_name harvested_species harvest_type harvest_season harvest_date title description model_name shopify_product_id status reviewer_remarks is_featured is_admin_story is_hall_of_honour consent_age_confirmed consent_age_text consent_guardian_confirmed consent_guardian_text published_at user_id category_id category { id slug label sort_order } author { id name } media { id url media_type sort_order created_at } created_at updated_at';
  const STORY_FIELDS = `edges { cursor node { ${STORY_NODE_FIELDS} } } pageInfo { hasNextPage hasPreviousPage startCursor endCursor }`;

  const QUERIES = {
    orders: `query GetMyOrders($filter: OrdersFilterInput) { myOrders(filter: $filter) { ${ORDER_FIELDS} } }`,
    ordersSummary: `query GetMyOrders($filter: OrdersFilterInput) { myOrders(filter: $filter) { ${ORDER_SUMMARY_FIELDS} } }`,
    ordersMinimal: `query GetMyOrders($filter: OrdersFilterInput) { myOrders(filter: $filter) { ${ORDER_MINIMAL_FIELDS} } }`,
    addresses: `query { myAddresses { ${ADDRESS_FIELDS} } }`,
    stories: `query MyStories($filter: StoriesFilterInput) { myStories(filter: $filter) { ${STORY_FIELDS} } }`,
    categories: 'query { categories { id slug label sort_order } }',
    createStory: 'mutation CreateStory($input: CreateStoryInput!) { createStory(input: $input) { id title media { id url media_type sort_order } } }',
    updateStory: `mutation UpdateStory($id: ID!, $input: UpdateStoryInput!) { updateStory(id: $id, input: $input) { ${STORY_NODE_FIELDS} } }`,
    deleteStory: 'mutation DeleteStory($id: ID!) { deleteStory(id: $id) }',
    requestChangePasswordOtp: 'mutation RequestChangePasswordOtp { requestChangePasswordOtp }',
    resendChangePasswordOtp: 'mutation ResendChangePasswordOtp { resendChangePasswordOtp }',
    verifyChangePasswordOtp: 'mutation VerifyChangePasswordOtp($input: VerifyChangePasswordOtpInput!) { verifyChangePasswordOtp(input: $input) }',
    changePassword: 'mutation ChangePassword($input: ChangePasswordInput!) { changePassword(input: $input) }',
    deactivateAccount: 'mutation DeactivateAccount { deactivateAccount }',
    deleteAccount: 'mutation DeleteAccount { deleteAccount }',
    signout: 'mutation Signout($input: SignoutInput!) { signout(input: $input) }',
    updateProfile: `mutation UpdateMe($input: UpdateProfileInput!) { updateMe(input: $input) { user { ${PROFILE_USER_FIELDS} } my_orders_link store_url } }`
  };

  function cleanFilter(filter) {
    return Object.keys(filter || {}).reduce((result, key) => {
      if (filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
        result[key] = filter[key];
      }

      return result;
    }, {});
  }

  function isShopifyOrderError(error) {
    return error && /shopify graphql/i.test(error.message || '');
  }

  // Surfaces the most specific message a GraphQL error carries. NestJS-style
  // validation errors nest the real detail (e.g. "category not found") inside
  // extensions.originalError.message while the top-level message stays generic.
  function extractGraphqlErrorMessage(error) {
    if (!error) return '';

    const extensions = error.extensions || {};
    const nested = extensions.originalError || extensions.exception || extensions.response || {};
    const detail = nested.message || extensions.message;

    if (Array.isArray(detail)) {
      return detail.filter(Boolean).join(' ');
    }

    if (detail) {
      return String(detail);
    }

    return error.message || '';
  }

  function extractToken(value) {
    if (!value) return '';

    const rawValue = String(value).trim();
    if (!rawValue) return '';

    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') {
        return extractToken(parsed.accessToken || parsed.token || parsed.authToken || parsed.jwt);
      }
    } catch (error) {
      // Stored token is already a plain string.
    }

    return rawValue.replace(/^Bearer\s+/i, '').trim();
  }

  const DEFAULT_ADDRESS_MUTATIONS = {
    create: `mutation CreateMyAddress($input: CreateShopifyCustomerAddressInput!) { createMyAddress(input: $input) { ${ADDRESS_FIELDS} } }`,
    update: `mutation UpdateMyAddress($input: UpdateShopifyCustomerAddressInput!) { updateMyAddress(input: $input) { ${ADDRESS_FIELDS} } }`,
    remove: 'mutation DeleteMyAddress($input: DeleteShopifyCustomerAddressInput!) { deleteMyAddress(input: $input) }'
  };

  class CustomerPortalApi {
    constructor(options) {
      const config = window.TKC && window.TKC.authConfig ? window.TKC.authConfig : {};
      const opts = options || {};

      this.endpoint = opts.endpoint || config.endpoint || DEFAULT_API_ENDPOINT;
      this.tokenKey = opts.tokenKey || config.tokenKey || 'tkcAccessToken';
      this.tokenFallbackKeys = [
        this.tokenKey,
        ...(Array.isArray(opts.tokenFallbackKeys) ? opts.tokenFallbackKeys : []),
        ...(Array.isArray(config.tokenFallbackKeys) ? config.tokenFallbackKeys : []),
        'tkcAccessToken',
        'tkcAssesstoken',
        'accessToken',
        'token',
        'authToken'
      ]
        .filter((key, index, keys) => key && keys.indexOf(key) === index);
      this.addressMutations = Object.assign({}, DEFAULT_ADDRESS_MUTATIONS, opts.addressMutations || {});
    }

    getToken() {
      for (let index = 0; index < this.tokenFallbackKeys.length; index += 1) {
        const token = extractToken(window.localStorage.getItem(this.tokenFallbackKeys[index]));
        if (token) return token;
      }

      return '';
    }

    requireToken() {
      const token = this.getToken();
      if (!token) {
        throw new Error('Please log in to view your portal.');
      }
      return token;
    }

    getAuthHeaders(extraHeaders) {
      const headers = Object.assign({
        Accept: 'application/json',
        'apollo-require-preflight': 'true',
        'ngrok-skip-browser-warning': 'true'
      }, extraHeaders || {});
      headers.Authorization = `Bearer ${this.requireToken()}`;
      return headers;
    }

    async request(query, variables) {
      if (!this.endpoint) {
        throw new Error('Customer portal API endpoint is missing.');
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ query, variables: variables || {} })
      });

      return this.parseResponse(response);
    }

    async parseResponse(response) {
      const payload = await response.json().catch(() => null);

      if (payload && payload.errors && payload.errors.length) {
        const messages = payload.errors.map(extractGraphqlErrorMessage).filter(Boolean);
        throw new Error(messages.join(' ') || 'Request failed. Please try again.');
      }

      if (!response.ok) {
        throw new Error((payload && payload.message) || 'Request failed. Please try again.');
      }

      return payload ? payload.data : null;
    }

    async myOrders(filter) {
      const variables = { filter: cleanFilter(filter) };

      try {
        const data = await this.request(QUERIES.orders, variables);
        return data && data.myOrders ? data.myOrders : { orders: [], pageInfo: {} };
      } catch (error) {
        if (!isShopifyOrderError(error)) throw error;
      }

      try {
        const data = await this.request(QUERIES.ordersSummary, variables);
        const result = data && data.myOrders ? data.myOrders : { orders: [], pageInfo: {} };
        result.orders = (result.orders || []).map((order) => Object.assign({ lineItems: [], lineItemsUnavailable: true }, order));
        return result;
      } catch (error) {
        if (!isShopifyOrderError(error)) throw error;
      }

      const data = await this.request(QUERIES.ordersMinimal, variables);
      const result = data && data.myOrders ? data.myOrders : { orders: [], pageInfo: {} };
      result.orders = (result.orders || []).map((order) => Object.assign({ lineItems: [], lineItemsUnavailable: true, orderDetailsLimited: true }, order));
      return result;
    }

    async myAddresses() {
      const data = await this.request(QUERIES.addresses);
      return data && data.myAddresses ? data.myAddresses : [];
    }

    async myStories(filter) {
      const data = await this.request(QUERIES.stories, { filter });
      return data && data.myStories ? data.myStories : { edges: [], pageInfo: {} };
    }

    async categories() {
      const data = await this.request(QUERIES.categories);
      return data && Array.isArray(data.categories) ? data.categories : [];
    }

    async createAddress(input) {
      return this.request(this.addressMutations.create, { input });
    }

    async updateAddress(id, input) {
      return this.request(this.addressMutations.update, { input: Object.assign({ addressId: id }, input) });
    }

    async deleteAddress(id) {
      return this.request(this.addressMutations.remove, { input: { addressId: id } });
    }

    async setDefaultAddress(id) {
      // No standalone mutation: defaulting is done through the update mutation.
      return this.request(this.addressMutations.update, { input: { addressId: id, setAsDefault: true } });
    }

    async signout(deviceId) {
      return this.request(QUERIES.signout, { input: { device_id: deviceId } });
    }

    async requestChangePasswordOtp() {
      return this.request(QUERIES.requestChangePasswordOtp);
    }

    async resendChangePasswordOtp() {
      return this.request(QUERIES.resendChangePasswordOtp);
    }

    async verifyChangePasswordOtp(otp) {
      return this.request(QUERIES.verifyChangePasswordOtp, { input: { otp } });
    }

    async changePassword(input) {
      return this.request(QUERIES.changePassword, { input });
    }

    async deactivateAccount() {
      const data = await this.request(QUERIES.deactivateAccount);
      return data ? data.deactivateAccount : null;
    }

    async deleteAccount() {
      const data = await this.request(QUERIES.deleteAccount);
      return data ? data.deleteAccount : null;
    }

    async updateProfile(input) {
      const data = await this.request(QUERIES.updateProfile, { input });
      return data ? data.updateMe : null;
    }

    // Normalizes a mixed list of media entries into the GraphQL `media` array,
    // collecting the entries that need a multipart file upload. Supported entries:
    //   - File/Blob, or { file: File }  -> uploaded via multipart ({ file: null, ... })
    //   - { id: '...' }                 -> existing media kept as-is (update only)
    //   - { url: '...' }                -> external link (e.g. YouTube)
    buildStoryMedia(mediaItems) {
      const entries = (mediaItems || []).filter(Boolean);
      const uploads = [];

      const media = entries.map((entry, index) => {
        const file = entry instanceof Blob ? entry : entry.file;
        const sortOrder = typeof entry.sort_order === 'number' ? entry.sort_order : index;

        if (file) {
          uploads.push({ file, mediaIndex: index });
          return {
            file: null,
            alt: entry.alt || file.name || `Media ${index + 1}`,
            media_type: file.type && file.type.indexOf('video/') === 0 ? 'VIDEO' : 'IMAGE',
            sort_order: sortOrder
          };
        }

        if (entry.id) {
          const item = { id: entry.id, sort_order: sortOrder };
          if (entry.alt) item.alt = entry.alt;
          return item;
        }

        const item = { url: entry.url, sort_order: sortOrder };
        if (entry.alt) item.alt = entry.alt;
        if (entry.media_type) item.media_type = entry.media_type;
        return item;
      });

      return { media, uploads };
    }

    async sendStoryMultipart(query, variables, uploads, rootField) {
      if (!this.endpoint) {
        throw new Error('Customer portal API endpoint is missing.');
      }

      const map = {};
      uploads.forEach((upload, uploadIndex) => {
        map[uploadIndex] = [`variables.input.media.${upload.mediaIndex}.file`];
      });

      const formData = new FormData();
      formData.append('operations', JSON.stringify({ query, variables }));
      formData.append('map', JSON.stringify(map));
      uploads.forEach((upload, uploadIndex) => {
        formData.append(String(uploadIndex), upload.file);
      });

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders({ 'apollo-require-preflight': 'true' }),
        body: formData
      });

      const data = await this.parseResponse(response);
      return data && data[rootField] ? data[rootField] : null;
    }

    async createStory(input, mediaItems) {
      const { media, uploads } = this.buildStoryMedia(mediaItems);
      const variables = { input: Object.assign({}, input, { media }) };
      return this.sendStoryMultipart(QUERIES.createStory, variables, uploads, 'createStory');
    }

    async updateStory(id, input, mediaItems) {
      const { media, uploads } = this.buildStoryMedia(mediaItems);
      const variables = { id, input: Object.assign({}, input, { media }) };
      return this.sendStoryMultipart(QUERIES.updateStory, variables, uploads, 'updateStory');
    }

    async deleteStory(id) {
      const data = await this.request(QUERIES.deleteStory, { id });
      return data ? data.deleteStory : null;
    }
  }

  window.TKC = window.TKC || {};
  window.TKC.CustomerPortalApi = CustomerPortalApi;
})();
